import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Wallet, IndianRupee, Shield, Zap, Sparkles } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { PaymentSuccessModal } from "./PaymentSuccessModal";
import { getRazorpayKeyId } from "@/config/env";
import { useParams } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import { getUserProfile, UserProfile } from "@/services/userService";
import { addFunds, CreateOrderResponse } from "@/services/walletService";
import { toast } from "sonner";
import { ensureRazorpayLoaded } from "@/utils/razorpay";
import { whitelabelConfig } from "@/config/whitelabel";

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  image?: string;
  order_id?: string; // Razorpay order ID from backend
  handler: (response: RazorpayResponse) => void;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  theme: {
    color: string;
  };
  modal?: {
    ondismiss?: () => void;
  };
}

interface WalletAddMoneyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddMoney: (amount: number) => void;
}

const quickAmounts = [500, 1000, 5000, 10000, 25000, 50000];
const MIN_WALLET_AMOUNT = 1;

export function WalletAddMoneyModal({
  isOpen,
  onClose,
  onAddMoney,
}: WalletAddMoneyModalProps) {
  const { isDark } = useTheme();
  const params = useParams();
  const { storeId } = params || {};
  const authState = useAppSelector((state) => state.auth);
  const { userId, adminAuth } = authState;
  
  const [amount, setAmount] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<{
    amount: number;
    transactionId: string;
  } | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const numericAmount = parseInt(amount) || 0;

  // Load user profile to get phone number for Razorpay prefill
  useEffect(() => {
    const fetchUserProfile = async () => {
      const userIdToUse = userId || adminAuth?.userId;
      if (!storeId || !userIdToUse) {
        return;
      }

      try {
        const profile = await getUserProfile(
          storeId as string,
          userIdToUse,
        );
        console.log("User profile loaded for Razorpay prefill:", profile);
        setUserProfile(profile);
      } catch (error) {
        console.error("Failed to load user profile:", error);
        // Non-critical error, continue without prefill
      }
    };

    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen, storeId, userId, adminAuth?.userId]);

  const handleQuickAmount = (quickAmount: number) => {
    setAmount(quickAmount.toString());
  };

  const handleAmountChange = (value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, "");
    setAmount(numericValue);
  };

  const handleAddMoney = async () => {
    if (!numericAmount || numericAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (numericAmount < MIN_WALLET_AMOUNT) {
      toast.error(`Minimum recharge amount is ₹${MIN_WALLET_AMOUNT}`);
      return;
    }

    if (numericAmount > 100000) {
      toast.error("Maximum recharge amount is ₹1,00,000");
      return;
    }

    const razorpayKeyId = getRazorpayKeyId();
    if (!razorpayKeyId) {
      toast.error("Payment gateway is not configured. Please contact support.");
      console.error(
        "VITE_RAZORPAY_KEY_ID is not set in environment variables",
      );
      return;
    }

    setIsProcessing(true);

    try {
      await ensureRazorpayLoaded();

      if (typeof window.Razorpay === "undefined") {
        throw new Error(
          "Payment gateway is not loaded. Please refresh the page and try again.",
        );
      }

      // Step 1: Create wallet order on backend
      console.log("Creating wallet order for amount:", numericAmount);
      const amountInPaise = numericAmount * 100; // Convert to paise
      const orderResponse = await addFunds(amountInPaise);
      
      console.log("Order created:", orderResponse);
      
      const orderData = orderResponse.data as CreateOrderResponse;
      const razorpayOrderId = orderData.orderId;
      
      if (!razorpayOrderId) {
        throw new Error("Failed to create order: No order ID received");
      }

      console.log("Razorpay Order ID:", razorpayOrderId);

      // Step 2: Log user profile for debugging
      console.log("Current userProfile state:", userProfile);
      console.log("Razorpay prefill data:", {
        name: userProfile?.name || "",
        email: userProfile?.email || "",
        contact: userProfile?.phone_number ? `+${userProfile.phone_number}` : "",
        fullProfile: userProfile,
      });

      // Step 3: Razorpay options with order_id from backend
      const options: RazorpayOptions = {
        key: razorpayKeyId,
        amount: amountInPaise, // Amount in paise
        currency: "INR",
        name: whitelabelConfig.payments.razorpayName,
        description: "Wallet Recharge",
        image: whitelabelConfig.logos.auth,
        order_id: razorpayOrderId, // Use order_id from backend
        handler: function (response: RazorpayResponse) {
          // Payment successful
          console.log("Payment successful:", response);

          // Store payment details for success modal
          setPaymentDetails({
            amount: numericAmount,
            transactionId: response.razorpay_payment_id,
          });

          // Show success modal with confetti
          setShowSuccessModal(true);

          // Update wallet balance
          onAddMoney(numericAmount);
          setAmount("");
          setIsProcessing(false);
        },
        prefill: {
          name: userProfile?.name || "",
          email: userProfile?.email || "",
          contact: userProfile?.phone_number ? `+${userProfile.phone_number}` : "",
        },
        theme: {
          color: "#5659ff", // Your primary color
        },
        modal: {
          ondismiss: function () {
            // Payment modal closed by user
            console.log("Payment cancelled by user");
            setIsProcessing(false);
            onClose(); // Close the wallet modal when Razorpay is dismissed
          },
        },
      };

      console.log("Initiating Razorpay with options:", options);

      // Step 4: Create Razorpay instance and open checkout
      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response: any) {
        // Payment failed
        console.error("Payment failed:", response.error);
        toast.error(`Payment failed: ${response.error.description || "Unknown error"}`);
        setIsProcessing(false);
        onClose(); // Close the wallet modal on payment failure
      });

      // Close the wallet modal before opening Razorpay to avoid z-index issues
      onClose();

      razorpay.open();
    } catch (error) {
      console.error("Error initiating payment:", error);
      toast.error(error instanceof Error ? error.message : "Failed to initiate payment. Please try again.");
      setIsProcessing(false);
    }
  };

  const resetAndClose = () => {
    setAmount("");
    setIsProcessing(false);
    onClose();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setPaymentDetails(null);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={resetAndClose}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-primary/10 dark:via-card dark:to-background backdrop-blur-xl border border-primary/20 dark:border-primary/30 shadow-2xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-[0.08] dark:opacity-[0.05]">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%235659ff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />
          </div>

          {/* Floating Elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-primary/30 to-white/90 dark:from-primary/10 dark:to-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-white/95 to-primary/20 dark:from-primary/8 dark:to-primary/4 rounded-full blur-3xl" />

          <DialogHeader className="pb-6 border-b border-primary/20 dark:border-border/40 relative z-10 w-fit shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-primary/20 to-primary/10 backdrop-blur-sm rounded-xl border border-primary/20">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-xl font-bold text-slate-900 dark:text-foreground flex items-center gap-2">
                  Add Money to Wallet
                  <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                </DialogTitle>
                <p className="text-sm text-slate-600 dark:text-muted-foreground">
                  Instantly add funds to your wallet
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-2 relative z-10 flex-1 overflow-y-auto pr-1">
            {/* Quick Amount Selection */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground">
                  Quick Select
                </h3>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {quickAmounts.map((quickAmount) => (
                  <Button
                    key={quickAmount}
                    variant={
                      amount === quickAmount.toString() ? "default" : "outline"
                    }
                    onClick={() => handleQuickAmount(quickAmount)}
                    className={`h-12 text-sm font-medium rounded-xl transition-all duration-200 ${
                      amount === quickAmount.toString()
                        ? "bg-primary text-primary-foreground shadow-lg scale-105 border-primary"
                        : "border-border/60 hover:bg-primary/5 hover:border-primary/30 hover:scale-105"
                    }`}
                  >
                    <IndianRupee className="h-3 w-3 mr-1" />
                    {quickAmount >= 1000
                      ? `${quickAmount / 1000}K`
                      : quickAmount}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom Amount Input */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground">
                  Enter Custom Amount
                </h3>
              </div>

              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center">
                  <IndianRupee className="h-6 w-6 text-primary" />
                </div>
                <Input
                  type="text"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="Enter amount"
                  className="pl-14 h-16 text-2xl font-bold text-center bg-white/50 dark:bg-card/50 backdrop-blur-sm border-primary/20 dark:border-border/60 focus:ring-primary/30 focus:border-primary rounded-xl shadow-inner text-slate-900 dark:text-foreground placeholder:text-slate-400 dark:placeholder:text-muted-foreground"
                />
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  {numericAmount > 0 && (
                    <Badge className="bg-primary/10 text-primary border-primary/20 font-semibold">
                      ₹
                      {numericAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Razorpay Badge */}
            <div className="flex items-center justify-center gap-2 p-4 bg-white/50 dark:bg-card/30 backdrop-blur-sm rounded-xl border border-primary/20 dark:border-border/40">
              <span className="text-sm text-slate-600 dark:text-muted-foreground">
                Powered by
              </span>
              <img
                src={isDark ? "/assets/razorpay-dark.png" : "/assets/razorpay-light.svg"}
                alt="Razorpay"
                height={24}
                className="h-6 w-auto"
              />
            </div>

            {/* Security Notice */}
            <div className="bg-green-100/50 dark:bg-green-500/10 backdrop-blur-sm border border-green-200 dark:border-green-500/30 rounded-xl p-3 flex items-start gap-3">
              <Shield className="h-5 w-5 text-green-600 dark:text-green-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-400">
                  Secure Transaction
                </p>
                <p className="text-xs text-green-700 dark:text-green-500">
                  Your payment is protected with bank-grade encryption
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-primary/20 dark:border-border/40 relative z-10 shrink-0">
            <Button
              variant="outline"
              onClick={resetAndClose}
              disabled={isProcessing}
              className="text-sm h-10 px-6 border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMoney}
              disabled={
                !numericAmount ||
                numericAmount < MIN_WALLET_AMOUNT ||
                isProcessing
              }
              className="bg-gradient-to-r from-primary to-primary/90 text-primary-foreground hover:from-primary/90 hover:to-primary/80 disabled:opacity-50 disabled:cursor-not-allowed text-sm h-10 px-6 shadow-lg rounded-lg flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Add ₹
                  {numericAmount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Success Modal with Confetti */}
      {paymentDetails && (
        <PaymentSuccessModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          amount={paymentDetails.amount}
          transactionId={paymentDetails.transactionId}
        />
      )}
    </>
  );
}
